import {Component, Plugins} from '@packages/systems/wfdl';
import type {
  RemoteSandboxActions,
  SandboxFileSystem,
  RemoteFile,
} from './actionCreators';
import {CssVariablesStateType} from '@packages/systems/core/utils/StyleTypes';
import {singleMemo, weakMemo} from '@packages/utilities/memo';
import {
  projectVariablesToTailwindTheme,
  projectDependenciesToPackageJson,
  projectComponentsToFiles,
  type ProjectedComponentFiles,
  type SourceFilesState,
  PROJECTED_PACKAGE_JSON_FILENAME,
  PROJECTED_THEME_FILENAME,
  ensureLeadingSlash,
  isComponentFilename,
  sourceFileSandboxPath,
  sandboxPathIndex,
} from '@packages/systems/code-libraries/core';
import {CreateSandboxResponse} from '@packages/domains/code-libraries/data-types';
import {
  REMOTE_SANDBOX_CREATED,
  REMOTE_SANDBOX_EXPIRED,
  REMOTE_SANDBOX_FILES_DELETED,
  REMOTE_SANDBOX_INITIATED,
  REMOTE_SANDBOX_SYNC_COMPLETED,
  REMOTE_SANDBOX_SYNC_STARTED,
  CODE_BUILD_STARTED,
  CODE_BUILD_SUCCEEDED,
  CODE_BUILD_FAILED,
  VCC_SOURCE_FILE_RENAMED,
  FUNCTIONS_GENERATION_MISSING,
} from './actionTypes';
import {REMOTE_FILESYSTEM_FILES_COMMITTED} from '@packages/systems/designer/actions';

export type RemoteSandboxState = {
  sandbox:
    | {status: 'CREATED'; value: CreateSandboxResponse}
    | {status: 'CREATING'}
    | {status: 'INIT'};
  job: {status: 'IDLE'} | {status: 'QUEUED'} | {status: 'RUNNING'};
  filesystem: SandboxFileSystem;
  // Functions generation the server reported as gone; the next build must not
  // replay a cached artifact that carries it.
  evictedFunctionsHash?: string;
};
export const initialRemoteSandboxState: RemoteSandboxState = {
  sandbox: {status: 'INIT'},
  job: {status: 'IDLE'},
  filesystem: {},
};

type ComponentFilesResult = {
  files: SandboxFileSystem;
  changed: boolean;
};

const buildFilesystemFromProjection = weakMemo(
  (filesystem: SandboxFileSystem) =>
    weakMemo((projected: ProjectedComponentFiles): ComponentFilesResult => {
      let changed = false;
      const files: SandboxFileSystem = {};

      for (const [filename, source] of projected) {
        const existing = filesystem[filename];
        // A PENDING_DELETE entry whose component reappears in the projection
        // (delete then re-create / undo, before the engine reconciles) must NOT
        // be reused as-is — the engine would still remove it from the sandbox.
        // Treat it as a fresh write so it's re-synced rather than deleted.
        if (
          existing &&
          existing.source === source &&
          existing.status !== 'PENDING_DELETE'
        ) {
          files[filename] = existing;
        } else {
          changed = true;
          files[filename] = {source, status: 'PENDING'};
        }
      }

      return {files, changed};
    })
);

const buildThemeFile = weakMemo((filesystem: SandboxFileSystem) =>
  singleMemo((themeVariables: string): {file: RemoteFile; changed: boolean} => {
    const existingTheme = filesystem[PROJECTED_THEME_FILENAME];
    if (existingTheme && existingTheme.source === themeVariables) {
      return {file: existingTheme, changed: false};
    }
    return {
      file: {source: themeVariables, status: 'PENDING'},
      changed: true,
    };
  })
);

const buildUserDependenciesFile = weakMemo((filesystem: SandboxFileSystem) =>
  singleMemo(
    (packageJsonSource: string): {file: RemoteFile; changed: boolean} => {
      const existingFile = filesystem[PROJECTED_PACKAGE_JSON_FILENAME];
      if (existingFile && existingFile.source === packageJsonSource) {
        return {file: existingFile, changed: false};
      }
      return {
        file: {source: packageJsonSource, status: 'PENDING'},
        changed: true,
      };
    }
  )
);

// Only code source files are projected into the remote sandbox. Agent
// instructions are REST-owned documents and must stay out of this filesystem.
// `codeChanged` gates the build; `changed` only gates re-projection. Iterating
// the index winners rather than raw values drops duplicates colliding on one
// path (`sandboxPathIndex` keeps the file with a persisted `path`).
const buildSourceFiles = weakMemo((filesystem: SandboxFileSystem) =>
  weakMemo(
    (
      sourceFiles: SourceFilesState
    ): {
      files: SandboxFileSystem;
      changed: boolean;
      codeChanged: boolean;
    } => {
      let changed = false;
      let codeChanged = false;
      const files: SandboxFileSystem = {};
      const instructionPaths = new Set(
        Object.values(sourceFiles)
          .filter((sourceFile) => sourceFile.kind !== 'code')
          .map(sourceFileSandboxPath)
      );

      for (const key of sandboxPathIndex(sourceFiles).values()) {
        const sourceFile = sourceFiles[key];
        if (!sourceFile || sourceFile.kind !== 'code') continue;
        const filename = sourceFileSandboxPath(sourceFile);
        // A stale kind-`code` duplicate can share an instruction's persisted
        // path. The instruction path remains excluded from the sandbox.
        if (instructionPaths.has(filename)) continue;
        const existing = filesystem[filename];
        // A PENDING_DELETE entry whose file reappears (rename back, undo) must
        // not be reused as-is, or the engine still removes it from the sandbox.
        if (
          existing &&
          existing.source === sourceFile.content &&
          existing.status !== 'PENDING_DELETE'
        ) {
          files[filename] = existing;
        } else {
          changed = true;
          if (sourceFile.kind === 'code') codeChanged = true;
          files[filename] = {source: sourceFile.content, status: 'PENDING'};
        }
      }

      return {files, changed, codeChanged};
    }
  )
);

/**
 * Reset every file to PENDING so a freshly created/recreated sandbox re-syncs
 * the full projection. `PENDING_DELETE` entries are dropped instead of reset:
 * a fresh sandbox never contained the deleted file, so there is nothing to
 * remove — resurrecting it as PENDING would re-write a file the user deleted.
 */
const resetFilesystemForNewSandbox = (
  filesystem: SandboxFileSystem
): SandboxFileSystem => {
  const next: SandboxFileSystem = {};
  for (const [path, file] of Object.entries(filesystem)) {
    if (file.status === 'PENDING_DELETE') continue;
    next[path] = {...file, status: 'PENDING'};
  }
  return next;
};

// Emitted on every projection regardless of state, so their absence from a
// projection never means "deleted" and they can never be orphans.
const ALWAYS_REPROJECTED_PATHS = new Set([
  PROJECTED_THEME_FILENAME,
  PROJECTED_PACKAGE_JSON_FILENAME,
]);

/**
 * Rebuild the full sandbox filesystem from local projections. Returns the same
 * reference (needsBuild false) if nothing changed, or a new object if it did.
 * needsBuild is true only for changes the build consumes — an instruction-file
 * edit re-projects without one.
 */
const projectFilesystem = (
  filesystem: SandboxFileSystem,
  componentsAfter: Component.ComponentMap,
  cssVariablesStateAfter: CssVariablesStateType,
  nextSlugMap: Record<string, string>,
  siteModuleDependencies: Plugins.PluginsType['siteModuleDependencies'],
  sourceFiles: SourceFilesState
): {filesystem: SandboxFileSystem; needsBuild: boolean} => {
  const projectedFiles = projectComponentsToFiles(componentsAfter);
  const componentResult =
    buildFilesystemFromProjection(filesystem)(projectedFiles);

  const nextThemeVariables = projectVariablesToTailwindTheme(
    cssVariablesStateAfter.variables
  )(cssVariablesStateAfter.variableCollections)(nextSlugMap);
  const themeResult = buildThemeFile(filesystem)(nextThemeVariables);

  const nextPackageJsonSource = projectDependenciesToPackageJson(
    siteModuleDependencies
  );
  const userDepsResult = buildUserDependenciesFile(filesystem)(
    nextPackageJsonSource
  );

  const sourceFilesResult = buildSourceFiles(filesystem)(sourceFiles);

  // A projected file present in the current filesystem but absent from the new
  // projection means its source was deleted in the Designer. Retain it with
  // status PENDING_DELETE (rather than silently dropping it) so the engine
  // removes it from the remote sandbox — otherwise the stale file lingers and,
  // if it failed to compile, keeps blocking every other component from loading.
  // Covers component `.webflow.tsx` files, the generated index manifest, and
  // plain source modules. The manifest disappears from the projection when the
  // last component is deleted (`projectComponentsToFiles` emits no index for an
  // empty map), and it imports the deleted components, so it must go too.
  //
  // Source files used to be exempt on the grounds that they were always
  // re-projected. That stopped being true once they became deletable (EXT-180):
  // a removed module is simply absent from the next projection, so skipping it
  // here left it in the sandbox forever, still imported and still compiled.
  // Only the theme and package.json are genuinely unconditional.
  const orphans: SandboxFileSystem = {};
  let hasNewOrphans = false;
  for (const [path, file] of Object.entries(filesystem)) {
    if (ALWAYS_REPROJECTED_PATHS.has(path)) continue;
    if (componentResult.files[path] || sourceFilesResult.files[path]) continue;
    orphans[path] =
      file.status === 'PENDING_DELETE'
        ? file
        : {...file, status: 'PENDING_DELETE'};
    // Only the transition into PENDING_DELETE is a change. Re-emitting an
    // already-flagged orphan unchanged keeps the short-circuit below intact, so
    // unrelated actions don't re-queue a build while the delete is in flight.
    if (file.status !== 'PENDING_DELETE') {
      hasNewOrphans = true;
    }
  }
  const hasOrphans = Object.keys(orphans).length > 0;

  // No components projected and nothing to delete (e.g. a site with no code
  // components): preserve the prior filesystem rather than projecting a
  // theme/package.json-only filesystem, which would spin up an empty sandbox.
  if (Object.keys(componentResult.files).length === 0 && !hasOrphans) {
    return {filesystem, needsBuild: false};
  }

  if (
    !componentResult.changed &&
    !themeResult.changed &&
    !userDepsResult.changed &&
    !sourceFilesResult.changed &&
    !hasNewOrphans
  ) {
    return {filesystem, needsBuild: false};
  }

  const nextFilesystem: SandboxFileSystem = {
    ...orphans,
    ...componentResult.files,
    ...sourceFilesResult.files,
  };
  nextFilesystem[PROJECTED_THEME_FILENAME] = themeResult.file;
  nextFilesystem[PROJECTED_PACKAGE_JSON_FILENAME] = userDepsResult.file;

  const needsBuild =
    componentResult.changed ||
    themeResult.changed ||
    userDepsResult.changed ||
    sourceFilesResult.codeChanged ||
    hasNewOrphans;

  return {filesystem: nextFilesystem, needsBuild};
};

// Paths this projection newly flagged for deletion. For a rename that is the
// name the file had before it moved -- the payload carries only the new one.
const newlyOrphanedPaths = (
  before: SandboxFileSystem,
  after: SandboxFileSystem
): string[] =>
  Object.keys(before).filter(
    (path) =>
      after[path]?.status === 'PENDING_DELETE' &&
      before[path]?.status !== 'PENDING_DELETE'
  );

// Paths this projection newly introduced. For a rename that is the new name,
// which matters when the new name satisfies an import the old one did not.
const newlyAddedPaths = (
  before: SandboxFileSystem,
  after: SandboxFileSystem
): string[] => Object.keys(after).filter((path) => before[path] === undefined);

/**
 * The names a relative specifier could use to reach `path`: the basename
 * without its extension, plus the containing folder when the file is that
 * folder's index — importers of a barrel write `'./helpers'`, never
 * `'./helpers/index'`, so matching only the basename would read every barrel
 * as unreferenced.
 */
const moduleNames = (path: string): string[] => {
  const segments = path.split('/').filter((segment) => segment !== '');
  const name = segments.pop() ?? '';
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name;
  const folder = segments.pop();
  return base === 'index' && folder !== undefined ? [base, folder] : [base];
};

// A module name only ever appears in a specifier right after a path separator
// or an opening quote, so those four prefixes are what distinguishes a
// reference from an identifier.
const REFERENCE_PREFIXES = ['/', "'", '"', '`'];

/**
 * Whether any file but `path` names its module the way a specifier would.
 *
 * Not import parsing: `require('./x')`, a template literal and a computed
 * specifier are all invisible to a specifier regex, and missing a reference is
 * the only direction that loses a build. Not a bare substring scan either, or
 * a file named `data.ts` would match every `const data =` on the site.
 *
 * Deliberately not a string-literal tokenizer, which is where this started.
 * Three rounds of review found three ways to desync one -- an apostrophe in a
 * double-quoted string, an escaped quote, then an unpaired apostrophe in JSX
 * text (`<p>Don't</p>`) pairing with a later specifier's quote. Every one read
 * a real import as absent. Matching a prefix instead has no boundaries to lose
 * track of, so that whole class is gone rather than patched.
 *
 * For a folder index this is coarser than "references `path`": any co-located
 * file counts, whether or not it imports anything, because a directory-only
 * specifier (`'.'`, `'..'`) carries no name a needle could match and only a
 * file already inside the folder could have written one. That over-counts --
 * a root-level index matches every path in the project -- but only ever
 * toward building.
 */
export const isModuleReferenced = (
  filesystem: SandboxFileSystem,
  path: string
): boolean => {
  const names = moduleNames(path).filter((name) => name !== '');
  if (names.length === 0) return true;
  if (names.includes('index')) {
    const folder = path.slice(0, path.lastIndexOf('/') + 1);
    if (
      Object.keys(filesystem).some(
        (otherPath) => otherPath !== path && otherPath.startsWith(folder)
      )
    ) {
      return true;
    }
  }
  const needles = names.flatMap((name) =>
    REFERENCE_PREFIXES.map((prefix) => prefix + name)
  );
  for (const [otherPath, file] of Object.entries(filesystem)) {
    if (otherPath === path) continue;
    if (needles.some((needle) => file.source.includes(needle))) return true;
  }
  return false;
};

export const RemoteSandboxStore = (
  state: RemoteSandboxState,
  action: RemoteSandboxActions,
  componentsAfter: Component.ComponentMap,
  cssVariablesStateAfter: CssVariablesStateType,
  nextSlugMap: Record<string, string>,
  siteModuleDependencies: Plugins.PluginsType['siteModuleDependencies'],
  sourceFiles: SourceFilesState
): RemoteSandboxState => {
  switch (action.type) {
    case REMOTE_SANDBOX_INITIATED: {
      return {
        ...state,
        sandbox: {status: 'CREATING'},
      };
    }
    case REMOTE_SANDBOX_CREATED: {
      return {
        ...state,
        sandbox: {status: 'CREATED', value: action.payload},
        filesystem: resetFilesystemForNewSandbox(state.filesystem),
      };
    }
    case REMOTE_SANDBOX_SYNC_STARTED: {
      const next = action.payload.reduce(
        (memo, target) => {
          if (!memo[target]) return memo;
          memo[target] = {...memo[target], status: 'SYNCING'};
          return memo;
        },
        {...state.filesystem}
      );
      return {
        ...state,
        filesystem: next,
      };
    }
    case REMOTE_SANDBOX_SYNC_COMPLETED: {
      const next = action.payload.reduce(
        (memo, target) => {
          const targetFile = memo[target];
          if (!targetFile || targetFile.status !== 'SYNCING') return memo;
          memo[target] = {...targetFile, status: 'SYNCED'};
          return memo;
        },
        {
          ...state.filesystem,
        }
      );
      return {
        ...state,
        filesystem: next,
      };
    }
    // The reason we catch this here and flag it as synced is to avoid
    // the default diffing case from causing another build with the exact
    // same results. Since we get the deps list back from the build itself
    // we can avoid a double build by marking this as eagerly synced
    case CODE_BUILD_SUCCEEDED: {
      return {
        ...state,
        filesystem: {
          ...state.filesystem,
          [PROJECTED_PACKAGE_JSON_FILENAME]: {
            source: projectDependenciesToPackageJson(siteModuleDependencies),
            status: 'SYNCED',
          },
        },
        // Only settle to IDLE if nothing was queued while this build ran. A
        // source write landing mid-build sets QUEUED (see the file-commit and
        // default cases below); resetting to IDLE here would discard that
        // request, and the engine only builds on QUEUED, so the newer source
        // would never be built. The canvas then keeps serving the previous
        // artifact with no error raised — EXT-1798.
        job: state.job.status === 'QUEUED' ? state.job : {status: 'IDLE'},
      };
    }
    case CODE_BUILD_STARTED: {
      return {
        ...state,
        // The engine reads the note before dispatching this, and one deletion
        // from its mirror is enough.
        evictedFunctionsHash: undefined,
        job: {status: 'RUNNING'},
      };
    }
    case FUNCTIONS_GENERATION_MISSING: {
      return {
        ...state,
        evictedFunctionsHash: action.payload.functionsHash,
        job: {status: 'QUEUED'},
      };
    }
    case CODE_BUILD_FAILED: {
      return {
        ...state,
        // Same as CODE_BUILD_SUCCEEDED: a rebuild queued during this build must
        // survive. The failed build's source is stale by definition once a
        // newer write has arrived.
        job: state.job.status === 'QUEUED' ? state.job : {status: 'IDLE'},
      };
    }
    case REMOTE_FILESYSTEM_FILES_COMMITTED: {
      // Upstream stores (e.g. SitePluginStore, codeComponentComponentMapReducer)
      // have already ingested the committed files, so local projections are
      // up-to-date. Rebuild from projections, then mark committed files as
      // SYNCED since the remote already has them.
      const {filesystem: nextFilesystem, needsBuild} = projectFilesystem(
        state.filesystem,
        componentsAfter,
        cssVariablesStateAfter,
        nextSlugMap,
        siteModuleDependencies,
        sourceFiles
      );
      if (nextFilesystem === state.filesystem) {
        return state;
      }

      // Mark committed files as SYNCED — the remote already has them.
      return {
        ...state,
        filesystem: Object.keys(action.payload.committed).reduce(
          (files, key) => {
            const path = ensureLeadingSlash(key);
            const file = files[path];
            if (file) {
              files[path] = {...file, status: 'SYNCED'};
            }
            return files;
          },
          nextFilesystem
        ),
        job: needsBuild ? {status: 'QUEUED'} : state.job,
      };
    }
    case REMOTE_SANDBOX_EXPIRED: {
      return {
        ...state,
        sandbox: {status: 'INIT'},
        filesystem: resetFilesystemForNewSandbox(state.filesystem),
        // A build this expired mid-run gets no completion action (no
        // codeBuildFailed/Succeeded), so job would otherwise stay stuck at
        // RUNNING/QUEUED forever. Same rule as CODE_BUILD_SUCCEEDED/FAILED
        // above: collapse to IDLE only if nothing was pending, since re-sync
        // resets file *status* to PENDING but not *source* -- content-based
        // needsBuild diffing at REMOTE_FILESYSTEM_FILES_COMMITTED would see no
        // change and never re-queue a build the content still needs.
        job: state.job.status === 'IDLE' ? state.job : {status: 'QUEUED'},
      };
    }
    case REMOTE_SANDBOX_FILES_DELETED: {
      // The engine removed these files from the remote sandbox; drop their
      // PENDING_DELETE entries from local state. When components remain, `job`
      // is left as-is — the deletion already queued a build, and clearing the
      // entry is what lets `allSynced` return true so that build can run.
      if (action.payload.length === 0) {
        return state;
      }
      const removed = new Set(action.payload.map(ensureLeadingSlash));
      const next: SandboxFileSystem = {};
      let changed = false;
      for (const [path, file] of Object.entries(state.filesystem)) {
        // Only drop entries that are still PENDING_DELETE. If a component was
        // deleted and then re-created at the same path before this removal was
        // acknowledged, the entry is now PENDING (a fresh write) — dropping it
        // would delete the live component's file from state.
        if (removed.has(path) && file.status === 'PENDING_DELETE') {
          changed = true;
          continue;
        }
        next[path] = file;
      }
      if (!changed) {
        return state;
      }
      // If the last component was just removed, the sandbox has no entry point
      // to build — settle `job` to IDLE so a queued build doesn't run against
      // an empty sandbox (which would never complete). When components remain,
      // leave `job` as-is so the rebuild of the survivors still runs.
      if (!Object.keys(next).some(isComponentFilename)) {
        return {...state, filesystem: next, job: {status: 'IDLE'}};
      }
      return {...state, filesystem: next};
    }
    case VCC_SOURCE_FILE_RENAMED: {
      const {filesystem: nextFilesystem, needsBuild} = projectFilesystem(
        state.filesystem,
        componentsAfter,
        cssVariablesStateAfter,
        nextSlugMap,
        siteModuleDependencies,
        sourceFiles
      );
      if (nextFilesystem === state.filesystem) {
        return state;
      }

      // An unreferenced module is never in the bundle graph, so renaming it
      // leaves the artifact identical and the cycle is waste. One that DID
      // break an import still builds: the TS2307 is how that surfaces.
      //
      // Both ends are checked. The new name matters because a rename can also
      // *repair* a dangling import -- renaming `fmt.ts` back to `format.ts` to
      // fix the break above changes the graph even though nothing named `fmt`.
      // A rename leaves importer text untouched, so `state.filesystem` is the
      // right thing to scan for either name.
      const orphaned = newlyOrphanedPaths(state.filesystem, nextFilesystem);
      const added = newlyAddedPaths(state.filesystem, nextFilesystem);
      const affectsBundle =
        orphaned.length === 0 ||
        orphaned.some((path) => isModuleReferenced(state.filesystem, path)) ||
        added.some((path) => isModuleReferenced(state.filesystem, path));

      return {
        ...state,
        filesystem: nextFilesystem,
        job: needsBuild && affectsBundle ? {status: 'QUEUED'} : state.job,
      };
    }
    default: {
      const {filesystem: nextFilesystem, needsBuild} = projectFilesystem(
        state.filesystem,
        componentsAfter,
        cssVariablesStateAfter,
        nextSlugMap,
        siteModuleDependencies,
        sourceFiles
      );
      if (nextFilesystem === state.filesystem) {
        return state;
      }

      return {
        ...state,
        filesystem: nextFilesystem,
        job: needsBuild ? {status: 'QUEUED'} : state.job,
      };
    }
  }
};
